import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MapPin, Loader2, CheckCircle2, ChevronRight, Building2, Stethoscope, FlaskConical, Pill, Activity, Phone, Calendar, FileText, Navigation, X, Search, Bookmark } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { referralApi } from "@/lib/api";

const HOSPITAL_ILLUS = "https://static.prod-images.emergentagent.com/jobs/67e85589-df3b-4949-9f26-d13809d545ec/images/b490053504a0730a121d8e0b7678f0b37d41c4f777f007034a0a06225cef15d4.jpeg";

const STATS = [
  { key: "hospital", label: "Hospital/Clinic", cats: ["hospital", "clinic"], Icon: Building2, color: "#2563EB", bg: "#E7EEFF" },
  { key: "doctor", label: "Doctors", cats: ["doctor"], Icon: Stethoscope, color: "#16A34A", bg: "#E7F7EE" },
  { key: "lab", label: "Path Lab", cats: ["lab"], Icon: FlaskConical, color: "#7C3AED", bg: "#F2EBFF" },
  { key: "diagnostic", label: "Diagnostic Centre", cats: ["diagnostic"], Icon: Activity, color: "#DB2777", bg: "#FCE7F3" },
  { key: "pharmacy", label: "Pharmacy", cats: ["pharmacy"], Icon: Pill, color: "#EA580C", bg: "#FFF0E6" },
];
const CAT_ICON = { hospital: "🏥", clinic: "🩺", lab: "🔬", diagnostic: "🩻", pharmacy: "💊", doctor: "👨‍⚕️" };
const ADD_TYPES = [
  { type: "hospital", label: "Hospital", icon: "🏥" },
  { type: "clinic", label: "Clinic", icon: "🩺" },
  { type: "lab", label: "Path Lab", icon: "🔬" },
  { type: "diagnostic", label: "Diagnostic Centre", icon: "🩻" },
  { type: "pharmacy", label: "Pharmacy", icon: "💊" },
];

export default function MyHospitalCard({ userId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);

  const [savedCat, setSavedCat] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState(null);
  const [code, setCode] = useState("");
  const [q, setQ] = useState("");
  const [onboarded, setOnboarded] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmProvider, setConfirmProvider] = useState(null);

  const load = useCallback(() => {
    if (!userId) return;
    referralApi
      .healthcareProviders(userId)
      .then((res) => {
        setProviders(res.data?.providers || []);
      })
      .catch(() => setProviders([]))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const countBy = (cats) => providers.filter((p) => cats.includes((p.category || "hospital").toLowerCase())).length;
  const pending = (p) => (p.verificationStatus || "").toLowerCase() === "pending";
  const openDetail = (p) => { if (!pending(p) && p.id) navigate(`/hospital/${p.id}`); };
  const directions = (p) => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([p.name, p.address, p.city, p.state].filter(Boolean).join(", "))}`, "_blank");

  const unpin = async (p) => {
    setProviders((prev) => prev.filter((x) => x.id !== p.id));
    try { await referralApi.unpinProvider(userId, p.id); } catch { load(); }
  };

  const loadOnboarded = useCallback((type, query = "") => {
    setListLoading(true);
    referralApi
      .onboardedByType({ type, q: query })
      .then((res) => setOnboarded(res.data?.providers || []))
      .catch(() => setOnboarded([]))
      .finally(() => setListLoading(false));
  }, []);

  const openAdd = (type = null) => {
    setAddType(type);
    setQ("");
    setCode("");
    setOnboarded([]);
    setAddOpen(true);
    if (type) loadOnboarded(type, "");
  };

  const chooseType = (type) => { setAddType(type); setQ(""); loadOnboarded(type, ""); };

  const onStatAdd = (key) => {
    setSavedCat(null);
    if (key === "doctor") { navigate("/find-care"); return; }
    if (key === "hospital") openAdd(null);
    else openAdd(key);
  };

  const saveByCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setSaving(true);
    try {
      await referralApi.savePreferred(userId, { referralCode: c, source: "referral", channel: "manual_code", category: "hospital" });
      toast.success("Provider added to your preferred providers");
      setAddOpen(false);
      setCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid referral code");
    } finally {
      setSaving(false);
    }
  };

  const confirmSave = async () => {
    const h = confirmProvider;
    if (!h) return;
    setSaving(true);
    try {
      await referralApi.savePreferred(userId, {
        providerId: h.id, source: "self_selected", category: addType || "hospital",
        name: h.name, city: h.city, state: h.state, address: h.address, logoUrl: h.logoUrl,
      });
      toast.success(`${h.name} added to your preferred providers`);
      setConfirmProvider(null);
      setAddOpen(false);
      load();
    } catch {
      toast.error("Could not add provider");
    } finally {
      setSaving(false);
    }
  };

  const renderSavedCard = (p) => (
    <Card key={p.id} className="rounded-3xl p-5 relative" data-testid={`provider-card-${p.id}`}>
      <button onClick={() => unpin(p)} className="absolute top-3 right-3 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-accent" data-testid={`provider-unpin-${p.id}`}><X className="h-4 w-4" /></button>
      <button onClick={() => openDetail(p)} disabled={pending(p)} className="flex items-start gap-4 w-full text-left" data-testid={`provider-open-${p.id}`}>
        <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center shrink-0 overflow-hidden text-2xl">
          {p.logoUrl ? <img src={p.logoUrl} alt={p.name} className="h-full w-full object-cover" /> : (CAT_ICON[(p.category || "hospital").toLowerCase()] || "🏥")}
        </div>
        <div className="min-w-0 flex-1 pr-6">
          <p className="text-lg font-black leading-tight" style={{ color: "#15296B" }}>{p.name}</p>
          {(p.city || p.state) && (
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{[p.city, p.state].filter(Boolean).join(", ")}</p>
          )}
          {pending(p) ? (
            <span className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Verification Pending</span>
          ) : (
            <span className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"><CheckCircle2 className="h-3.5 w-3.5" /> Saved Provider</span>
          )}
        </div>
        {!pending(p) && <span className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><ChevronRight className="h-5 w-5 text-primary" /></span>}
      </button>
      <div className="border-t border-border mt-4 pt-4 grid grid-cols-4 gap-2">
        {[
          { k: "call", Icon: Phone, label: "Call", onClick: () => openDetail(p), disabled: pending(p) },
          { k: "book", Icon: Calendar, label: "Book Appointment", onClick: () => openDetail(p), disabled: pending(p) },
          { k: "share", Icon: FileText, label: "Share Reports", onClick: () => navigate("/records") },
          { k: "directions", Icon: Navigation, label: "Get Directions", onClick: () => directions(p) },
        ].map((a) => (
          <button key={a.k} onClick={a.onClick} disabled={a.disabled} className="flex flex-col items-center gap-1.5 disabled:opacity-40" data-testid={`provider-${a.k}-${p.id}`}>
            <span className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center"><a.Icon className="h-4 w-4 text-primary" /></span>
            <span className="text-[11px] font-semibold text-slate-700 text-center leading-tight">{a.label}</span>
          </button>
        ))}
      </div>
    </Card>
  );

  if (loading) return <Skeleton className="h-64 rounded-3xl" data-testid="my-hospital-loading" />;

  const savedStat = STATS.find((s) => s.key === savedCat);
  const savedList = savedStat ? providers.filter((p) => savedStat.cats.includes((p.category || "hospital").toLowerCase())) : [];

  return (
    <div className="space-y-4" data-testid="my-healthcare-network">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-3xl" style={{ background: "#EAF2FF", border: "1px solid #DCE6FF", minHeight: 150 }}>
        <div className="relative z-10 p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-2xl overflow-hidden bg-white shrink-0 shadow-sm">
              <img src={HOSPITAL_ILLUS} alt="Hospital" className="h-full w-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-2xl font-black leading-tight" style={{ color: "#15296B" }} data-testid="my-providers-heading">My Healthcare Network</h3>
              <p className="text-sm font-semibold mt-1" style={{ color: "#45526E" }}>Your trusted care providers, all in one place</p>
            </div>
            <Button className="rounded-2xl shrink-0 hidden sm:flex" style={{ backgroundColor: "#2563EB" }} onClick={() => openAdd(null)} data-testid="my-providers-add-btn">
              <Plus className="h-4 w-4" /> Add Provider
            </Button>
          </div>
          <Button className="rounded-2xl w-full mt-4 sm:hidden" style={{ backgroundColor: "#2563EB" }} onClick={() => openAdd(null)}>
            <Plus className="h-4 w-4" /> Add Provider
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {STATS.map((s) => (
          <button key={s.key} onClick={() => setSavedCat(s.key)} data-testid={`provider-stat-${s.key}`} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left hover:shadow-sm transition-all">
            <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: s.bg }}>
              <s.Icon className="h-5 w-5" style={{ color: s.color }} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xl font-black" style={{ color: "#15296B" }}>{countBy(s.cats)}</p>
              <p className="text-[11px] text-muted-foreground font-semibold leading-tight">{s.label}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        ))}
      </div>

      {/* Saved-by-category popup */}
      <Dialog open={!!savedCat} onOpenChange={(o) => !o && setSavedCat(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto rounded-3xl" data-testid="saved-providers-dialog">
          <DialogHeader>
            <DialogTitle className="text-left" data-testid="saved-dialog-title">{savedStat?.label} · Saved</DialogTitle>
            <DialogDescription className="text-left">Your saved {savedStat?.label?.toLowerCase()} providers.</DialogDescription>
          </DialogHeader>
          {savedList.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No {savedStat?.label?.toLowerCase()} saved yet.</p>
          ) : (
            <div className="space-y-3">{savedList.map(renderSavedCard)}</div>
          )}
          <Button className="rounded-2xl w-full" onClick={() => onStatAdd(savedCat)} data-testid={`saved-add-${savedCat}`}>
            <Plus className="h-4 w-4" /> Add {savedStat?.label}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Add provider dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto rounded-3xl" data-testid="add-provider-dialog">
          <DialogHeader>
            <DialogTitle className="text-left">{addType ? `Add a ${ADD_TYPES.find((t) => t.type === addType)?.label}` : "Add a Provider"}</DialogTitle>
            <DialogDescription className="text-left">
              {addType ? "Tap Save on a provider to add it under your preferred providers." : "Pick the type of provider you'd like to add."}
            </DialogDescription>
          </DialogHeader>

          {!addType ? (
            <div className="grid grid-cols-2 gap-2">
              {ADD_TYPES.map((t) => (
                <button key={t.type} onClick={() => chooseType(t.type)} className="flex items-center gap-2 rounded-2xl border border-border p-3 text-left hover:bg-accent" data-testid={`add-type-${t.type}`}>
                  <span className="text-xl">{t.icon}</span>
                  <span className="font-semibold text-sm">{t.label}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              <button onClick={() => { setAddType(null); setOnboarded([]); }} className="text-sm font-bold text-primary flex items-center gap-1" data-testid="add-type-back">
                <ChevronRight className="h-4 w-4 rotate-180" /> Change type
              </button>

              {addType === "hospital" && (
                <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-3">
                  <p className="text-xs font-bold text-indigo-800">🏥 Have a referral code?</p>
                  <div className="flex gap-2 mt-2">
                    <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Enter code" className="rounded-xl" data-testid="my-hospital-code-input" />
                    <Button className="rounded-xl shrink-0" onClick={saveByCode} disabled={!code.trim() || saving} data-testid="my-hospital-save">Link</Button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 rounded-2xl bg-muted px-3 h-11">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input value={q} onChange={(e) => { setQ(e.target.value); loadOnboarded(addType, e.target.value); }} placeholder={`Search ${ADD_TYPES.find((t) => t.type === addType)?.label.toLowerCase()}s`} className="flex-1 bg-transparent outline-none text-sm" data-testid="add-search-input" />
              </div>

              {listLoading ? (
                <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : onboarded.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No partner providers found.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto space-y-2">
                  {onboarded.map((h) => (
                    <div key={h.id} className="flex items-center gap-3 rounded-2xl border border-border p-3" data-testid={`add-onboarded-${h.id}`}>
                      <span className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center text-lg shrink-0">{ADD_TYPES.find((t) => t.type === addType)?.icon}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{h.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{h.address || [h.city, h.state].filter(Boolean).join(", ")}</p>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full shrink-0 border-primary text-primary" onClick={() => setConfirmProvider(h)} data-testid={`add-save-${h.id}`}>
                        <Bookmark className="h-3.5 w-3.5" /> Save
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm add to preferred */}
      <Dialog open={!!confirmProvider} onOpenChange={(o) => !o && setConfirmProvider(null)}>
        <DialogContent className="max-w-sm rounded-3xl text-center" data-testid="confirm-save-dialog">
          <div className="flex flex-col items-center">
            <span className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center"><Bookmark className="h-6 w-6 text-primary" /></span>
            <DialogHeader className="items-center mt-3">
              <DialogTitle className="text-center">Add to preferred providers?</DialogTitle>
              <DialogDescription className="text-center">
                <span className="font-bold text-primary block mt-1">{confirmProvider?.name}</span>
                Confirm to add this under your preferred healthcare providers.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" className="rounded-2xl" onClick={() => setConfirmProvider(null)} disabled={saving}>Cancel</Button>
            <Button className="rounded-2xl" onClick={confirmSave} disabled={saving} data-testid="confirm-save-provider">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
